import React from 'react';
import { ArrowRight, Phone, Mail, MapPin, Linkedin, Facebook, Instagram } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import NewsletterSignup from './NewsletterSignup';

const NewFooter = () => {
  const { language } = useLanguage();
  const sl = language === 'sl';

  // Google Maps embed URL for Celjska cesta 47, Rogatec
  const mapEmbedUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2764.8876035547835!2d15.697841776892902!3d46.22924197111392!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x476567f3a0ccb0e7%3A0x8e0b1f1a85a1c3e1!2sCeljska%20cesta%2047%2C%203252%20Rogatec%2C%20Slovenia!5e0!3m2!1sen!2sus!4v1695000000000!5m2!1sen!2sus";

  return (
    <footer id="contact" className="bg-[#0E1114] text-[#F4EFE6]" data-testid="footer">
      {/* CTA Section */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-[120px] py-20 lg:py-[120px]">
        <div className="text-center max-w-[1040px] mx-auto">
          <span className="text-[13px] font-bold tracking-[2.5px] text-[#C9A462] block mb-6">
            {sl ? 'VAŠ NASLEDNJI KORAK' : 'YOUR NEXT STEP'}
          </span>
          <h2 className="font-serif text-[40px] sm:text-[56px] lg:text-[84px] leading-[1] tracking-[-1.5px] mb-6">
            {sl ? (
              <>Vsak mesec čakanja je še en mesec <span className="italic text-[#C9A462]">papirjev, ki jih ne bi bilo treba urejati vam.</span></>
            ) : (
              <>Every month of waiting is another month of <span className="italic text-[#C9A462]">paperwork you shouldn't have to deal with.</span></>
            )}
          </h2>
          <p className="text-[17px] lg:text-[19px] text-[#C9C3B7] leading-[1.6] max-w-[700px] mx-auto mb-8">
            {sl 
              ? 'Rezervirajte brezplačen 30-minutni posvet še danes. V najslabšem primeru odidete z jasnim načrtom in ponudbo — brez stroškov.'
              : 'Book a free 30-minute consultation today. At worst, you leave with a clear plan and a quote — at no cost.'}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#offer"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#offer')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="flex items-center gap-3 bg-[#C9A462] text-[#0E1114] font-bold text-[17px] px-9 py-5 no-underline hover:bg-[#B8944F] transition-colors"
            >
              {sl ? 'Rezerviram brezplačen posvet' : 'Book a free consultation'}
              <ArrowRight className="w-[18px] h-[18px]" />
            </a>
            <a
              href="https://api.whatsapp.com/send?phone=38651438441"
              target="_blank"
              rel="noopener noreferrer"
              className="border border-[#454C54] text-[#F4EFE6] font-semibold text-[17px] px-7 py-5 no-underline hover:bg-[#1a1d21] transition-colors"
            >
              {sl ? 'Pišite nam na WhatsApp' : 'Message us on WhatsApp'}
            </a>
          </div>
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="border-t border-[#2A3036]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-[120px]">
          <NewsletterSignup />
        </div>
      </div>

      {/* Map & Contact Section */}
      <div className="border-t border-[#2A3036]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-[120px] py-12">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Map */}
            <div className="w-full lg:w-2/3 h-[300px] lg:h-[400px] bg-[#1a1d21] overflow-hidden" data-testid="google-map">
              <iframe
                src={mapEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={sl ? "Lokacija General Ledger pisarne" : "General Ledger office location"}
                className="grayscale hover:grayscale-0 transition-all duration-500"
              />
            </div>
            
            {/* Contact Info Card */}
            <div className="w-full lg:w-1/3 bg-[#15181C] p-8 flex flex-col justify-center">
              <span className="text-[13px] font-bold tracking-[2.5px] text-[#C9A462] block mb-4">
                {sl ? 'OBIŠČITE NAS' : 'VISIT US'}
              </span>
              <h3 className="font-serif text-2xl lg:text-3xl mb-4">GENERAL LEDGER d.o.o.</h3>
              
              <div className="flex flex-col gap-3 text-[#B9B3A7] mb-6">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#C9A462] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="m-0">Celjska cesta 47</p>
                    <p className="m-0">3252 Rogatec, {sl ? 'Slovenija' : 'Slovenia'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#C9A462] flex-shrink-0" />
                  <a href="tel:+38638184060" className="text-[#D8D2C6] hover:text-white transition-colors">
                    +386 3 818 40 60
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#C9A462] flex-shrink-0" />
                  <a href="mailto:info@general-ledger.eu" className="text-[#D8D2C6] hover:text-white transition-colors">
                    info@general-ledger.eu
                  </a>
                </div>
              </div>
              
              <p className="text-sm text-[#7E786D] mb-4">
                {sl ? 'Delovni čas: Pon–pet 08:00–16:00' : 'Working hours: Mon–Fri 08:00–16:00'}
                <br />
                {sl ? 'Brezplačno parkiranje pred vrati' : 'Free parking at the entrance'}
              </p>
              
              <a
                href="https://www.google.com/maps/dir//Celjska+cesta+47,+3252+Rogatec,+Slovenia"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#C9A462] font-semibold text-sm hover:text-[#E0BC7A] transition-colors"
              >
                {sl ? 'Pridobi navodila za pot' : 'Get directions'}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-[#2A3036]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-[120px] py-10">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-8">
            {/* Logo & Description */}
            <div className="flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
              <img 
                src="/images/logo-white.png" 
                alt="GENERAL LEDGER d.o.o." 
                className="h-[56px] w-auto"
                loading="lazy"
                width="160"
                height="56"
              />
              <p className="text-sm text-[#7E786D] max-w-[400px] m-0">
                {sl 
                  ? 'Računovodski servis od leta 2003. Govorimo slovensko, angleško, srbohrvaško in špansko.'
                  : 'Accounting firm since 2003. We speak Slovenian, English, Serbo-Croatian and Spanish.'}
              </p>
            </div>
            
            {/* Social Media */}
            <div className="flex items-center gap-3">
              <a
                href="https://www.linkedin.com/company/general-ledger-doo"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#1a1d21] hover:bg-[#C9A462] text-[#B9B3A7] hover:text-[#0E1114] rounded-full flex items-center justify-center transition-all duration-300"
                aria-label="LinkedIn"
                data-testid="social-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://www.facebook.com/GeneralLedgerDOO"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#1a1d21] hover:bg-[#C9A462] text-[#B9B3A7] hover:text-[#0E1114] rounded-full flex items-center justify-center transition-all duration-300"
                aria-label="Facebook"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/generalledger.si"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#1a1d21] hover:bg-[#C9A462] text-[#B9B3A7] hover:text-[#0E1114] rounded-full flex items-center justify-center transition-all duration-300"
                aria-label="Instagram"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-[#2A3036]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-[120px] py-5 flex flex-col sm:flex-row justify-between items-center gap-4 text-[13px] text-[#7E786D]">
          <span>© 2026 GENERAL LEDGER d.o.o. · {sl ? 'Slovenija' : 'Slovenia'} · EU</span>
          <div className="flex gap-4">
            <Link to="/zasebnost" className="hover:text-[#B9B3A7]">{sl ? 'Zasebnost' : 'Privacy'}</Link>
            <Link to="/pogoji" className="hover:text-[#B9B3A7]">{sl ? 'Pogoji' : 'Terms'}</Link>
            <Link to="/piskotki" className="hover:text-[#B9B3A7]">{sl ? 'Piškotki' : 'Cookies'}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default NewFooter;
